import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  buttonBaseClasses,
  FormControl,
  FormControlLabel,
  List,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Checkbox, Typography } from 'cprime-foundational-components';
import { find, findIndex, filter, includes, isEmpty } from 'lodash';
import { AutoSizer, List as VirtualizedList } from 'react-virtualized';
import { useIsMobile } from 'hooks';
import CascadingFiltersListItem from '../CascadingFiltersListItem/CascadingFiltersListItem';
import EllipsisWithTooltip from '../../../EllipsisWithTooltip/EllipsisWithTooltip';
import { MIN_DATE } from '../../utils';

const isCheckedHandler = ({ sectionCascadingFiltersLocal, field, value }) => {
  if (isEmpty(sectionCascadingFiltersLocal)) {
    if (field === 'quickFilters') {
      return false;
    }
    return true;
  } else {
    const fieldObj = find(sectionCascadingFiltersLocal, { field, value });
    return fieldObj ? fieldObj.isChecked : false;
  }
};

const getNumOfSectionCheckedItems = (sectionCascadingFiltersLocal, numOfSectionItems, options) => {
  const uncheckedItems = filter(sectionCascadingFiltersLocal, {
    isChecked: false,
  });
  if (!isEmpty(uncheckedItems)) {
    return numOfSectionItems - uncheckedItems.length;
  }
  return options.length;
};

const getOption = (displayName) => (
  <EllipsisWithTooltip tooltipText={displayName}>
    <Typography
      component="label"
      variant="body2"
      sx={{
        width: '99%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {displayName}
    </Typography>
  </EllipsisWithTooltip>
);

const CascadingFiltersListItemAll = ({
  options,
  config,
  isAllSectionItemsChecked,
  isAllIntermediate,
  setAllCheckbox,
}) => {
  if (config?.hideAllOption) {
    return;
  }

  return (
    <CascadingFiltersListItem
      isChecked={isAllSectionItemsChecked}
      isIndeterminate={isAllIntermediate}
      itemText={
        <Typography variant="body1" component="span">{`All (${options.length})`}</Typography>
      }
      setCheckbox={() => setAllCheckbox()}
      disableRipple
      size="small"
      sx={{
        '&.MuiButtonBase-root': {
          height: '1.5rem',
        },
      }}
    />
  );
};
CascadingFiltersListItemAll.propTypes = {
  options: PropTypes.array,
  config: PropTypes.object,
  isAllSectionItemsChecked: PropTypes.bool,
  isAllIntermediate: PropTypes.bool,
  setAllCheckbox: PropTypes.func,
};

const cloneAndRemoveUpstream = ({
  sectionFiled,
  cascadingFiltersLocal,
  cascadingFiltersConfig,
}) => {
  const sectionsToRemove = [];
  cascadingFiltersConfig.reduce(
    (prev, current) => {
      if (prev.passedCurrentSection) {
        sectionsToRemove.push(current.field);
        return { passedCurrentSection: true };
      }
      return { passedCurrentSection: sectionFiled === current.field };
    },
    { passedCurrentSection: false },
  );
  if (!isEmpty(sectionsToRemove)) {
    return filter(cascadingFiltersLocal, ({ field }) => !includes(sectionsToRemove, field));
  }
  return [...cascadingFiltersLocal];
};

const RadioItemVirtualized = ({ index, style, hasRadioAllOption, options }) => {
  if (hasRadioAllOption && index === 0) {
    return (
      <FormControlLabel
        value=""
        control={<Radio size="small" sx={{ marginRight: '0.5rem' }} />}
        sx={{
          m: 0,
          px: 2,
          py: 0,
          height: '1.5rem',
          width: '100%',
          [`&& .${buttonBaseClasses.root}`]: { p: 0 },
        }}
        label={
          <Typography variant="body1" component="span">
            All
          </Typography>
        }
      />
    );
  }

  const optionIndex = hasRadioAllOption ? index - 1 : index;
  const option = options[optionIndex];
  if (!option) {
    return null;
  }

  return (
    <div style={style}>
      <FormControlLabel
        value={option.value}
        disabled={option.isDisabled}
        control={<Radio size="small" sx={{ marginRight: '0.5rem' }} />}
        sx={{
          m: 0,
          px: 2,
          py: 0,
          height: '1.5rem',
          width: '100%',
          overflow: 'hidden',
          [`&& .${buttonBaseClasses.root}`]: { p: 0 },
          '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden' },
        }}
        label={
          <EllipsisWithTooltip tooltipText={option.displayName}>
            <Typography
              variant="body2"
              component="span"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: 'block',
              }}
            >
              {option.displayName}
            </Typography>
          </EllipsisWithTooltip>
        }
      />
    </div>
  );
};
RadioItemVirtualized.propTypes = {
  index: PropTypes.number,
  style: PropTypes.object,
  hasRadioAllOption: PropTypes.bool,
  options: PropTypes.array,
};

export const CascadingFiltersList = ({
  options,
  fixedHeight,
  isFiltered,
  setCascadingFiltersLocal,
  cascadingFiltersLocal,
  virtualizedList,
  cascadingFiltersConfig,
  isCascadingFilterOff,
  businessSalesMonthData,
  error,
  setError,
}) => {
  const MAX_RANGE_MONTHS = 12;
  const [startYear, setStartYear] = useState(null);
  const [endYear, setEndYear] = useState(null);
  const [singleMonth, setSingleMonth] = useState(false);
  const [monthRange, setMonthRange] = useState(['', '']);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const config = cascadingFiltersConfig?.find((config) => config.field === options?.[0]?.field);
  const isMobile = useIsMobile();
  const businessSalesMonthRaw = businessSalesMonthData?.all?.businessSalesMonth;
  const businessSalesMonthMaxDate = businessSalesMonthRaw
    ? dayjs(String(businessSalesMonthRaw), 'YYYYMM')
    : null;

  const startDatePickerMaxDate =
    endYear && (!businessSalesMonthMaxDate || endYear.isBefore(businessSalesMonthMaxDate))
      ? endYear
      : businessSalesMonthMaxDate ?? dayjs(new Date());

  const VIRTUALIZED_RADIO_FIELDS = ['accessoriesFactories', 'accessoriesPostProductions'];
  const isVirtualizedRadio = config?.isRadio && VIRTUALIZED_RADIO_FIELDS.includes(config?.field);

  const hasRadioAllOption = config?.isRadio && !config?.hideRadioAllOption;

  const renderRadioRow = useCallback(
    (props) => (
      <RadioItemVirtualized {...props} hasRadioAllOption={hasRadioAllOption} options={options} />
    ),
    [hasRadioAllOption, options],
  );

  const sectionFiled = options?.[0]?.field;
  const sectionCascadingFiltersLocal = filter(cascadingFiltersLocal, { field: sectionFiled });

  const isMonthRange = cascadingFiltersLocal?.some(
    (filter) => filter.value === 'Month Range' && filter.isChecked,
  );
  const isStartBeforeMin = startYear && dayjs(startYear).isBefore(MIN_DATE);
  const isEndBeforeMin = endYear && dayjs(endYear).isBefore(MIN_DATE);
  const isRangeInvalid =
    isMonthRange &&
    ((singleMonth && !startYear) ||
      (!singleMonth && (!startYear || !endYear)) ||
      (!singleMonth && startYear && endYear && dayjs(startYear).isAfter(dayjs(endYear))) ||
      isStartBeforeMin ||
      isEndBeforeMin);

  useEffect(() => {
    setError((prev) => ({ ...prev, 'Time Period': isRangeInvalid }));
  }, [isRangeInvalid]);

  useEffect(() => {
    if (isMonthRange) {
      const businessSalesMonthFilter = cascadingFiltersLocal?.find(
        (filter) => filter.field === 'businessSalesMonth',
      );

      if (businessSalesMonthFilter && businessSalesMonthFilter.value) {
        const months = Array.isArray(businessSalesMonthFilter.value)
          ? businessSalesMonthFilter.value
          : [businessSalesMonthFilter.value];

        if (months.length > 0 && months[0]) {
          const startDate = dayjs(months[0], 'YYYYMM');
          if (!startYear || !startYear.isSame(startDate, 'month')) {
            setStartYear(startDate);
          }

          if (months.length === 1) {
            if (!singleMonth) {
              setSingleMonth(true);
            }
            setMonthRange([months[0]]);
          } else if (months.length > 1 && months[1]) {
            const endDate = dayjs(months[1], 'YYYYMM');
            if (!endYear || !endYear.isSame(endDate, 'month')) {
              setEndYear(endDate);
            }
            if (singleMonth) {
              setSingleMonth(false);
            }
            setMonthRange([months[0], months[1]]);
          }
        }
      }
    } else {
      if (startYear || endYear || singleMonth) {
        setStartYear(null);
        setEndYear(null);
        setSingleMonth(false);
        setMonthRange(['', '']);
      }
    }
  }, [isMonthRange]);

  useEffect(() => {
    if (startYear || endYear) {
      const cascadingFiltersLocalClone = [...cascadingFiltersLocal];
      const fieldIndex = findIndex(cascadingFiltersLocalClone, {
        field: 'businessSalesMonth',
      });
      const businessSalesMonths = {
        field: 'businessSalesMonth',
        value: singleMonth ? monthRange.slice(0, 1) : monthRange,
        isChecked: true,
      };

      if (fieldIndex === -1) {
        cascadingFiltersLocalClone.push(businessSalesMonths);
      } else {
        cascadingFiltersLocalClone[fieldIndex] = businessSalesMonths;
      }

      setCascadingFiltersLocal(cascadingFiltersLocalClone);
    }
  }, [monthRange, singleMonth]);

  if (isEmpty(options)) {
    return null;
  }

  const toggleSingleMonthCheckbox = () => {
    const newSingleMonth = !singleMonth;
    setSingleMonth(newSingleMonth);

    if (newSingleMonth) {
      setEndYear(null);
      setMonthRange((prev) => [prev[0]]);
    } else {
      setMonthRange((prev) => [prev[0], '']);
    }
  };

  const handleMonthRangeSelection = (value, index) => {
    const formattedMonth = dayjs(value).format('YYYYMM');
    setMonthRange((prev) => {
      const next = [...prev];
      next[index] = formattedMonth;
      return next;
    });
  };

  const addMonths = (date, months) => (date ? date.add(months, 'month') : null);

  const subMonths = (date, months) => (date ? date.subtract(months, 'month') : null);

  const handleRadioSelection = (e) => {
    const cascadingFiltersLocalClone = [...cascadingFiltersLocal];
    const field = options[0]?.field;
    const value = e.target.value;
    const fieldIndex = findIndex(cascadingFiltersLocalClone, { field, value });

    // ALL radio option
    if (value === '') {
      let sectionFields = cascadingFiltersLocalClone.map((option) => {
        if (option.field === field) {
          return {
            ...option,
            isChecked: false,
          };
        }
        return option;
      });

      // Reset businessSalesMonth when clearing timePeriod selection
      if (field === 'timePeriod') {
        sectionFields = sectionFields.filter((option) => option.field !== 'businessSalesMonth');
        setStartYear(null);
        setEndYear(null);
        setSingleMonth(false);
        setMonthRange(['', '']);
      }

      return setCascadingFiltersLocal(sectionFields);
    }

    if (fieldIndex > -1) {
      let sectionFields = cascadingFiltersLocalClone.map((option, index) => {
        if (option.field === field) {
          return {
            ...option,
            isChecked: fieldIndex === index,
          };
        }
        return option;
      });

      // Reset businessSalesMonth when switching away from "Month Range"
      if (field === 'timePeriod' && value !== 'Month Range') {
        sectionFields = sectionFields.filter((option) => option.field !== 'businessSalesMonth');
        setStartYear(null);
        setEndYear(null);
        setSingleMonth(false);
        setMonthRange(['', '']);
      }

      setCascadingFiltersLocal(sectionFields);
    } else {
      const sectionFields = options.map((option) => ({
        field: option.field,
        value: option.value,
        isChecked: e.target.value === option.value,
      }));

      const otherFieldsFilters = cascadingFiltersLocalClone.filter(
        (option) => option.field !== field,
      );

      // Reset businessSalesMonth when switching away from "Month Range"
      if (field === 'timePeriod' && value !== 'Month Range') {
        const filteredCascading = otherFieldsFilters.filter(
          (option) => option.field !== 'businessSalesMonth',
        );
        setStartYear(null);
        setEndYear(null);
        setSingleMonth(false);
        setMonthRange(['', '']);
        setCascadingFiltersLocal([...filteredCascading, ...sectionFields]);
      } else {
        setCascadingFiltersLocal([...otherFieldsFilters, ...sectionFields]);
      }
    }
  };

  const setCheckboxHandler = ({ fieldObj, isChecked }) => {
    const { field, value } = fieldObj;
    const cascadingFiltersLocalClone = isCascadingFilterOff
      ? [...cascadingFiltersLocal]
      : cloneAndRemoveUpstream({
          sectionFiled,
          cascadingFiltersLocal,
          cascadingFiltersConfig,
        });

    const fieldIndex = findIndex(cascadingFiltersLocalClone, { field, value });

    if (fieldIndex > -1) {
      cascadingFiltersLocalClone[fieldIndex] = {
        field,
        value,
        isChecked: !isChecked,
      };
      setCascadingFiltersLocal(cascadingFiltersLocalClone);
    } else {
      const sectionFields = options.map((opt) =>
        value === opt.value
          ? { field, value, isChecked: !opt.isChecked }
          : { field: opt.field, value: opt.value, isChecked: opt.isChecked },
      );
      setCascadingFiltersLocal([...cascadingFiltersLocalClone, ...sectionFields]);
    }
  };

  const numOfSectionItems = options?.length;
  const numOfSectionCheckedItems = getNumOfSectionCheckedItems(
    isFiltered ? sectionCascadingFiltersLocal : options,
    numOfSectionItems,
    options,
  );

  const isAllIntermediate =
    numOfSectionItems > numOfSectionCheckedItems && numOfSectionCheckedItems > 0;

  const isAllSectionItemsChecked =
    !isAllIntermediate && numOfSectionItems === numOfSectionCheckedItems;

  const setAllCheckbox = () => {
    const cascadingFiltersLocalClone = cloneAndRemoveUpstream({
      sectionFiled,
      cascadingFiltersLocal,
      cascadingFiltersConfig,
    });
    if (isEmpty(sectionCascadingFiltersLocal)) {
      const sectionFields = options.map((opt) => ({
        field: opt.field,
        value: opt.value,
        isChecked: false,
      }));
      setCascadingFiltersLocal([...cascadingFiltersLocalClone, ...sectionFields]);
    } else {
      const isOneFalse = !!find(sectionCascadingFiltersLocal, { isChecked: false });
      const otherSectionsFilters = filter(
        cascadingFiltersLocalClone,
        (item) => item.field !== sectionFiled,
      );
      const sectionFields = options.map((opt) => ({
        field: opt.field,
        value: opt.value,
        isChecked: isOneFalse,
      }));
      setCascadingFiltersLocal([...otherSectionsFilters, ...sectionFields]);
    }
  };

  const ListItemVirtualized = ({ index, key, style }) => {
    if (index === 0) {
      return (
        <CascadingFiltersListItemAll
          key={key}
          style={style}
          options={options}
          isAllSectionItemsChecked={isAllSectionItemsChecked}
          isAllIntermediate={isAllIntermediate}
          setAllCheckbox={setAllCheckbox}
        />
      );
    }
    const fieldObj = options[index - 1];
    if (fieldObj) {
      const isChecked = isCheckedHandler({
        sectionCascadingFiltersLocal,
        field: fieldObj.field,
        value: fieldObj.value,
      });

      return (
        <div style={style} key={key}>
          <CascadingFiltersListItem
            key={fieldObj.value}
            isChecked={isChecked}
            setCheckbox={() =>
              setCheckboxHandler({
                fieldObj,
                isChecked: isChecked,
              })
            }
            disableRipple
            size="small"
            itemText={getOption(fieldObj.displayName)}
          />
        </div>
      );
    }
  };
  ListItemVirtualized.propTypes = {
    index: PropTypes.number,
    key: PropTypes.string,
    style: PropTypes.object,
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <List
        sx={{
          padding: 0,
          height: `${fixedHeight && 'calc(100dvh - 22.5rem)'}`,
          width: isMobile ? '12rem' : '16rem',
        }}
      >
        {config?.isRadio && isVirtualizedRadio && (
          <FormControl
            sx={{
              height: '100%',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <RadioGroup
              value={sectionCascadingFiltersLocal?.find((option) => option.isChecked)?.value || ''}
              onChange={handleRadioSelection}
              sx={{ height: '100%' }}
            >
              <AutoSizer>
                {({ height }) => (
                  <VirtualizedList
                    height={height}
                    width={isMobile ? 200 : 256}
                    rowCount={(hasRadioAllOption ? 1 : 0) + options.length}
                    rowHeight={24}
                    rowRenderer={renderRadioRow}
                  />
                )}
              </AutoSizer>
            </RadioGroup>
          </FormControl>
        )}
        {config?.isRadio && !isVirtualizedRadio && (
          <FormControl
            sx={{
              height: '100%',
              width: '100%',
              overflow: config.field === 'timePeriod' ? 'auto' : 'scroll',
            }}
          >
            <RadioGroup
              value={sectionCascadingFiltersLocal?.find((option) => option.isChecked)?.value || ''}
              onChange={handleRadioSelection}
            >
              {!config.hideRadioAllOption && (
                <FormControlLabel
                  value=""
                  control={<Radio size="small" sx={{ marginRight: '0.5rem' }} />}
                  sx={{
                    m: 0,
                    px: 2,
                    py: 0,
                    height: '1.5rem',
                    width: '100%',
                    [`&& .${buttonBaseClasses.root}`]: { p: 0 },
                  }}
                  label={
                    <Typography variant="body1" component="span">
                      All
                    </Typography>
                  }
                />
              )}
              {options.map((option) => {
                const isOptionChecked = isCheckedHandler({
                  sectionCascadingFiltersLocal,
                  field: option.field,
                  value: option.value,
                });
                const isStartRangeInvalid =
                  (!startYear && error?.['Time Period']) ||
                  (!singleMonth &&
                    startYear &&
                    endYear &&
                    dayjs(startYear).isAfter(dayjs(endYear)));
                let startHelperText = '';
                if (isStartBeforeMin) {
                  startHelperText = 'Min. month is Jan 2001';
                } else if (isStartRangeInvalid) {
                  startHelperText = 'Select a month to apply filter';
                }
                const startDateError =
                  (!startYear && error?.['Time Period']) ||
                  isStartBeforeMin ||
                  (!singleMonth &&
                    startYear &&
                    endYear &&
                    dayjs(startYear).isAfter(dayjs(endYear)));
                const isEndRangeInvalid =
                  (!endYear && error?.['Time Period']) ||
                  (startYear && endYear && dayjs(startYear).isAfter(dayjs(endYear)));
                let endHelperText = '';
                if (isEndBeforeMin) {
                  endHelperText = 'Min. month is Jan 2001';
                } else if (isEndRangeInvalid) {
                  endHelperText = 'Select a month to apply filter';
                }
                return (
                  <React.Fragment key={`${option.value}-${option.displayName}`}>
                    <FormControlLabel
                      value={option.value}
                      disabled={option.isDisabled}
                      control={<Radio size="small" sx={{ marginRight: '0.5rem' }} />}
                      sx={{
                        m: 0,
                        px: 2,
                        py: 0,
                        height: '1.5rem',
                        width: '100%',
                        overflow: 'hidden',
                        [`&& .${buttonBaseClasses.root}`]: { p: 0 },
                        '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden' },
                      }}
                      label={
                        <EllipsisWithTooltip tooltipText={option.displayName}>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {option.displayName}
                          </Typography>
                        </EllipsisWithTooltip>
                      }
                    />
                    {isOptionChecked && option.displayName === 'Month Range' && (
                      <Stack px={3} gap={1}>
                        <Typography variant="label">Start Business Sales Month*</Typography>
                        <DatePicker
                          open={startDateOpen}
                          onOpen={() => setStartDateOpen(true)}
                          onClose={() => setStartDateOpen(false)}
                          format="MMMM YYYY"
                          views={['year', 'month']}
                          openTo="month"
                          minDate={(() => {
                            const rangeMin = endYear
                              ? subMonths(endYear, MAX_RANGE_MONTHS)
                              : MIN_DATE;
                            return rangeMin?.isBefore(MIN_DATE) ? MIN_DATE : rangeMin ?? MIN_DATE;
                          })()}
                          maxDate={startDatePickerMaxDate}
                          value={startYear}
                          onChange={(value) => {
                            if (!value?.isValid()) {
                              return;
                            }
                            setStartYear(value);
                            handleMonthRangeSelection(value, 0);
                            if (
                              !value.isBefore(MIN_DATE) &&
                              dayjs(value).isSame(dayjs(), 'month')
                            ) {
                              setSingleMonth(true);
                              setEndYear(null);
                              setMonthRange(() => [dayjs(value).format('YYYYMM')]);
                            }
                          }}
                          sx={{ width: '13rem' }}
                          slotProps={{
                            inputAdornment: {
                              position: 'start',
                            },
                            openPickerIcon: {
                              sx: { fontSize: '1rem', color: 'text.secondary' },
                            },
                            textField: {
                              size: 'small',
                              placeholder: 'Select month',
                              onClick: () => setStartDateOpen(true),
                              sx: {
                                '& .MuiOutlinedInput-input': {
                                  color: 'text.secondary',
                                },
                                '& .MuiOutlinedInput-input::placeholder': {
                                  color: 'text.disabled',
                                  opacity: 1,
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderWidth: '1px',
                                    borderColor: 'action.active',
                                  },
                              },
                              InputProps: {
                                endAdornment: null,
                              },
                              error: startDateError,
                              helperText: startHelperText,
                              FormHelperTextProps: {
                                sx: { ml: 0 },
                              },
                            },
                            popper: {
                              placement: 'bottom-start',
                              modifiers: [
                                {
                                  name: 'flip',
                                  enabled: false,
                                },
                              ],
                              sx: {
                                '.MuiPickersPopper-paper': {
                                  marginTop: '0.25rem',
                                },
                                '.MuiDateCalendar-root': {
                                  height: 'auto',
                                },
                              },
                            },
                          }}
                        />
                        {!singleMonth && (
                          <>
                            <Typography variant="label">End Business Sales Month*</Typography>
                            <DatePicker
                              open={endDateOpen}
                              onOpen={() => setEndDateOpen(true)}
                              onClose={() => setEndDateOpen(false)}
                              format="MMMM YYYY"
                              views={['year', 'month']}
                              openTo="month"
                              minDate={(() => {
                                const min = startYear ?? MIN_DATE;
                                return min.isBefore(MIN_DATE) ? MIN_DATE : min;
                              })()}
                              maxDate={(() => {
                                const cap = businessSalesMonthMaxDate ?? dayjs(new Date());
                                const rangeEnd = startYear
                                  ? addMonths(startYear, MAX_RANGE_MONTHS)
                                  : cap;
                                return rangeEnd.isAfter(cap) ? cap : rangeEnd;
                              })()}
                              value={endYear}
                              onChange={(value) => {
                                if (!value?.isValid()) {
                                  return;
                                }
                                setEndYear(value);
                                handleMonthRangeSelection(value, 1);
                              }}
                              sx={{ width: '13rem' }}
                              slotProps={{
                                inputAdornment: {
                                  position: 'start',
                                },
                                openPickerIcon: {
                                  sx: { fontSize: '1rem', color: 'text.secondary' },
                                },
                                textField: {
                                  size: 'small',
                                  placeholder: 'Select month',
                                  onClick: () => setEndDateOpen(true),
                                  sx: {
                                    '& .MuiOutlinedInput-input': {
                                      color: 'text.secondary',
                                    },
                                    '& .MuiOutlinedInput-input::placeholder': {
                                      color: 'text.disabled',
                                      opacity: 1,
                                    },
                                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                      {
                                        borderWidth: '1px',
                                        borderColor: 'action.active',
                                      },
                                  },
                                  InputProps: {
                                    endAdornment: null,
                                  },
                                  error:
                                    (!endYear && error?.['Time Period']) ||
                                    isEndBeforeMin ||
                                    (startYear &&
                                      endYear &&
                                      dayjs(startYear).isAfter(dayjs(endYear))),
                                  helperText: endHelperText,
                                  FormHelperTextProps: {
                                    sx: { ml: 0 },
                                  },
                                },
                                popper: {
                                  placement: 'bottom-start',
                                  modifiers: [
                                    {
                                      name: 'flip',
                                      enabled: false,
                                    },
                                  ],
                                  sx: {
                                    '.MuiPickersPopper-paper': {
                                      marginTop: '0.25rem',
                                    },
                                    '.MuiDateCalendar-root': {
                                      height: 'auto',
                                    },
                                  },
                                },
                              }}
                            />
                          </>
                        )}
                        <Checkbox
                          label="Single Month Range"
                          checked={singleMonth}
                          onChange={toggleSingleMonthCheckbox}
                        />
                      </Stack>
                    )}
                  </React.Fragment>
                );
              })}
            </RadioGroup>
          </FormControl>
        )}
        {!config?.isRadio &&
          (virtualizedList ? (
            <AutoSizer>
              {({ height }) => (
                <VirtualizedList
                  height={height}
                  width={isMobile ? 200 : 256}
                  rowCount={options.length + 1}
                  rowHeight={21}
                  rowRenderer={ListItemVirtualized}
                />
              )}
            </AutoSizer>
          ) : (
            <>
              <CascadingFiltersListItemAll
                options={options}
                config={config}
                isAllSectionItemsChecked={isAllSectionItemsChecked}
                isAllIntermediate={isAllIntermediate}
                setAllCheckbox={setAllCheckbox}
              />
              {options.map((fieldObj) => {
                const isChecked = isCheckedHandler({
                  sectionCascadingFiltersLocal,
                  field: fieldObj.field,
                  value: fieldObj.value,
                });
                return (
                  <CascadingFiltersListItem
                    key={fieldObj.value}
                    isChecked={isChecked}
                    setCheckbox={() =>
                      setCheckboxHandler({
                        fieldObj,
                        isChecked: isChecked,
                      })
                    }
                    itemText={getOption(fieldObj.displayName)}
                  />
                );
              })}
            </>
          ))}
      </List>
    </LocalizationProvider>
  );
};

CascadingFiltersList.propTypes = {
  options: PropTypes.array,
  fixedHeight: PropTypes.bool,
  isFiltered: PropTypes.bool,
  setCascadingFiltersLocal: PropTypes.func,
  cascadingFiltersLocal: PropTypes.array,
  virtualizedList: PropTypes.bool,
  cascadingFiltersConfig: PropTypes.array,
  isCascadingFilterOff: PropTypes.bool,
  businessSalesMonthData: PropTypes.object,
  error: PropTypes.object,
  setError: PropTypes.func,
};
